/**
 * Business — goals (the plan) and initiatives (the board) that move the company forwards. Admins only.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Compass, Plus, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BizContext } from './BizContext';
import { errMsg, isOpenGoal } from './lib';
import { Eyebrow } from './bits';
import { BizOverview } from './BizOverview';
import { BizBoard } from './BizBoard';
import { BizPlan } from './BizPlan';
import { BizQuickWins } from './BizQuickWins';
import { BizSettings } from './BizSettings';
import { CreateDialog } from './CreateDialog';
import { Workspace } from './workspace/Workspace';
import './business.css';

const HERO = (year) => ({
  overview: { h: ['Where the business', 'is heading.'], p: 'Not the day-to-day. The things that move the company forwards, and the plan they ladder up to.' },
  board: { h: ['What’s on', 'the go.'], p: 'Every initiative from idea to done. Drag a card as it moves, open it for the journal.' },
  plan: { h: ['The plan', `for ${year}.`], p: 'One big goal, four quarterly goals, six minis — and the future goals parked for next year. Written down so it gets done.' },
  quickwins: { h: ['Quick', 'wins.'], p: 'High impact, low effort — the things worth knocking out this week.' },
  settings: { h: ['Nudges', '& tags.'], p: 'How often we get reminded, what counts as worth a nudge, and the labels we file things under.' },
});

const VIEWS = [
  { key: 'overview', label: 'Overview' },
  { key: 'board', label: 'Board' },
  { key: 'plan', label: 'Plan' },
  { key: 'quickwins', label: 'Quick wins' },
  { key: 'settings', label: 'Nudges & tags' },
];

export default function Business() {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [view, setViewState] = useState(params.get('view') || 'overview');
  const setView = useCallback((v) => { setViewState(v); setParams((p) => { const n = new URLSearchParams(p); n.set('view', v); return n; }, { replace: true }); }, [setParams]);
  const [data, setData] = useState(null);
  const [ws, setWs] = useState(null); // { kind: 'goal'|'item', id }
  const [create, setCreate] = useState(null); // { kind, preset }

  useEffect(() => { if (user && !isAdmin) navigate('/projects'); }, [user, isAdmin, navigate]);

  const load = useCallback(async () => {
    try { const r = await api.get('/business/data'); setData(r.data); return r.data; }
    catch (e) { toast.error(errMsg(e, 'Could not load the Business page')); return null; }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const skipOpen = useRef(null); // the ?open value we just closed — ignore it until the URL catches up
  const paramsRef = useRef(params); paramsRef.current = params;
  const setOpenParam = useCallback((id) => setParams((p) => { const n = new URLSearchParams(p); if (id) n.set('open', id); else n.delete('open'); return n; }, { replace: true }), [setParams]);
  const openWs = useCallback((kind, id) => { skipOpen.current = null; setWs({ kind, id }); setOpenParam(id); }, [setOpenParam]);
  const closeWs = useCallback(() => { skipOpen.current = paramsRef.current.get('open'); setWs(null); setOpenParam(null); }, [setOpenParam]);
  useEffect(() => {
    const open = params.get('open');
    if (open && data && !ws && open !== skipOpen.current) {
      if (data.initiatives.some((i) => i.id === open)) setWs({ kind: 'item', id: open });
      else if (data.goals.some((g) => g.id === open)) setWs({ kind: 'goal', id: open });
      else closeWs();
    }
  }, [params, data, ws, closeWs]);

  const items = useMemo(() => data?.initiatives || [], [data]);
  const goals = useMemo(() => data?.goals || [], [data]);

  // "+ New → New goal" from the top bar lands here with ?new=goal
  useEffect(() => {
    if (params.get('new') === 'goal') {
      setCreate({ kind: 'goal', preset: {} });
      setParams((p) => { const n = new URLSearchParams(p); n.delete('new'); return n; }, { replace: true });
    }
  }, [params, setParams]);

  const run = useCallback(async (fn, ok, opts = {}) => {
    try { const r = await fn(); await load(); if (ok && !opts.silent) toast.success(ok); return r?.data ?? true; }
    catch (e) { toast.error(errMsg(e, 'Something went wrong')); return null; }
  }, [load]);
  const pingBell = () => window.dispatchEvent(new Event('admin-notifications:refresh'));

  const actions = useMemo(() => ({
    reload: load,
    createItem: (body, opts) => run(() => api.post('/business/initiatives', body), 'Initiative added', opts),
    patchItem: (id, body, opts) => run(() => api.patch(`/business/initiatives/${id}`, body), 'Saved', opts),
    moveItem: async (id, status) => {
      setData((d) => d && { ...d, initiatives: d.initiatives.map((x) => (x.id === id ? { ...x, status } : x)) });
      try { await api.patch(`/business/initiatives/${id}`, { status }); await load(); } catch (e) { toast.error(errMsg(e, 'Could not move it')); load(); }
    },
    archiveItem: async (id) => { if (await run(() => api.delete(`/business/initiatives/${id}`), 'Removed')) closeWs(); },
    createGoal: (body, opts) => run(() => api.post('/business/goals', body), 'Goal added', opts),
    patchGoal: (id, body, opts) => run(() => api.patch(`/business/goals/${id}`, body), 'Saved', opts),
    archiveGoal: async (id) => { if (await run(() => api.delete(`/business/goals/${id}`), 'Goal removed')) closeWs(); },
    reviewGoal: (id, body) => run(() => api.post(`/business/goals/${id}/review`, body), 'Review filed — next sit-down booked'),
    saveNudge: (body) => run(() => api.put('/business/settings/nudge', body), 'Nudge settings saved'),
    sendNow: async () => { const r = await run(() => api.post('/business/nudge/send-now')); if (!r) return; pingBell(); toast.info(r.line_count ? `Nudge sent — ${r.line_count} item${r.line_count === 1 ? '' : 's'}${r.email_status === 'sent' ? ', emailed too' : ''}` : 'Nothing to nudge about right now'); },
    saveTags: (tags) => run(() => api.put('/business/settings/tags', { tags }), 'Tags saved'),
  }), [api, run, load, closeWs]);

  const year = data?.year || new Date().getFullYear();
  const ctx = useMemo(() => ({
    api, actions, owners: data?.owners || [], tags: data?.tags || [], goals, initiatives: items, today: data?.today, year,
    tagMap: Object.fromEntries((data?.tags || []).map((t) => [t.key, t])), goalMap: Object.fromEntries(goals.map((g) => [g.id, g])),
  }), [api, actions, data, goals, items, year]);

  const openItem = (id) => openWs('item', id);
  const openGoal = (id) => openWs('goal', id);
  const newItem = (preset) => setCreate({ kind: 'item', preset: preset || {} });
  const newGoal = (preset) => setCreate({ kind: 'goal', preset: preset && !preset.nativeEvent ? preset : {} });
  const hero = HERO(year)[view] || HERO(year).overview;
  const wsStart = useMemo(() => ws && { kind: ws.kind, id: ws.id }, [ws]);
  const counts = { board: items.filter((i) => i.status !== 'done').length, plan: goals.filter(isOpenGoal).length };

  return (
    <BizContext.Provider value={ctx}>
      <div className="biz min-h-screen" data-testid="business-page">
        <div className="relative max-w-[1440px] mx-auto px-6 py-8">
          <header className="mb-8">
            <div className="max-w-2xl biz-rise" key={view} data-testid="biz-hero">
              <div className="flex items-center gap-2 mb-2"><Compass className="w-4 h-4" style={{ color: 'var(--apricot-deep)' }} /><Eyebrow className="tracking-[0.2em]">Lux Developments · Business</Eyebrow></div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="biz-hero-title">{hero.h[0]}<br />{hero.h[1]}</h1>
              <p className="text-base mt-4 max-w-xl" style={{ color: 'var(--ink-2)' }} data-testid="biz-hero-sub">{hero.p}</p>
            </div>
          </header>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
            <nav className="flex flex-wrap items-center gap-1 -mx-1" data-testid="biz-views">
              {VIEWS.map((v) => (
                <button key={v.key} type="button" className="biz-pill" data-active={view === v.key} onClick={() => setView(v.key)} data-testid={`biz-view-${v.key}`}>
                  {v.label}{counts[v.key] != null && <span className="biz-count" data-testid={`biz-view-count-${v.key}`}>{counts[v.key]}</span>}
                </button>
              ))}
            </nav>
            <div className="flex gap-2 ml-auto">
              <button type="button" className="biz-btn" onClick={() => newGoal()} data-testid="biz-new-goal"><Plus className="w-4 h-4" /> New goal</button>
              <button type="button" className="biz-btn biz-btn-accent" onClick={() => newItem()} data-testid="biz-new-initiative"><Plus className="w-4 h-4" /> New initiative</button>
            </div>
          </div>

          {!data ? (
            <div className="py-20 text-center" style={{ color: 'var(--ink-3)' }} data-testid="biz-loading"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
          ) : (
            <div key={view} className="biz-rise">
              {view === 'overview' && <BizOverview items={items} goals={goals} nudge={data.nudge} onOpen={openItem} onOpenGoal={openGoal} setView={setView} onNewGoal={newGoal} onNewItem={newItem} />}
              {view === 'board' && <BizBoard items={items} onOpen={openItem} onMove={actions.moveItem} onNew={newItem} />}
              {view === 'plan' && <BizPlan goals={goals} onOpen={openGoal} onNew={newGoal} />}
              {view === 'quickwins' && <BizQuickWins items={items} onOpen={openItem} />}
              {view === 'settings' && <BizSettings nudge={data.nudge} tags={data.tags} lastNudge={data.last_nudge} onSaveNudge={actions.saveNudge} onSendNow={actions.sendNow} onSaveTags={actions.saveTags} onOpen={openItem} onOpenGoal={openGoal} onPlan={() => setView('plan')} />}
            </div>
          )}
        </div>

        {wsStart && data && <Workspace start={wsStart} onClose={closeWs} onNewItem={newItem} onNavigate={setOpenParam} />}
        {create && <CreateDialog kind={create.kind} preset={create.preset} onClose={() => setCreate(null)} onCreated={(doc) => { setCreate(null); openWs(create.kind, doc.id); }} />}
      </div>
    </BizContext.Provider>
  );
}
